import {
  type GetTourSessionsBreakdownArgs,
  type TourActivity,
  useInfiniteScroll,
  useLazyGetTourSessionsBreakdownQuery,
} from '@carboncopyco/data';
import { TextBase, TextCaption, TooltipBubble } from '@carboncopyco/ui';
import {
  formatPlural,
  getTimezoneName,
  secondsToHHmmss,
} from '@carboncopyco/utils';
import { useEffect, useMemo, useState } from 'react';
import { TimestampCol } from '../../../../Dashboard/SessionBreakdownComponents';
import type { CsvParams } from '../../../../Dashboard/SessionBreakdownComponents/types';
import type { Col } from '../../../../FixedTable';
import { useUrlState } from '../../../../utils';
import { SessionsBreakdownTable } from '../../../SessionsBreakdownTable';
import type { UrlState } from '../../../types';
import { useTourPageProps } from '../../TourPageContext';
import { CompletedStepsCol } from '../CompletedStepsCol';
import { TourLinksFilter } from './TourLinksFilter';

const cols: Col<TourActivity>[] = [
  {
    alignment: 'left',
    title: 'Date & time',
    width: 150,
    maxContentWidth: 130,
    render: ({ timestamp }) => <TimestampCol timestamp={timestamp} />,
  },
  {
    alignment: 'left',
    title: 'Visitor',
    width: 220,
    maxContentWidth: 190,
    render: ({ user }) => (
      <div className="flex flex-col justify-center h-10">
        <TooltipBubble
          title={user?.name}
          placement="top-start"
          disabled={!user?.name}
        >
          <div className="truncate">
            <TextBase
              label={user?.name || 'Anonymous'}
              color="text-ds-natural-600"
              ellipsis
            />
          </div>
        </TooltipBubble>
        <TooltipBubble
          title={user?.email}
          placement="top-start"
        >
          <div className="truncate text-ds-natural">
            <TextCaption
              label={user?.email}
              ellipsis
            />
          </div>
        </TooltipBubble>
      </div>
    ),
  },
  {
    alignment: 'left',
    title: 'Tour name',
    width: 250,
    maxContentWidth: 220,
    render: ({ share }) => (
      <TooltipBubble
        title={share.shareName}
        placement="top"
      >
        <div className="truncate">
          <TextBase
            label={share.shareName}
            color="text-ds-natural-600"
            ellipsis
          />
        </div>
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: 'Duration',
    width: 120,
    render: (tour) => (
      <TextBase
        label={secondsToHHmmss(tour.durationInSeconds)}
        color="text-ds-natural-600"
      />
    ),
  },
  {
    alignment: 'right',
    title: 'Completed steps',
    width: 200,
    render: ({ completedSteps, totalSteps }) => (
      <CompletedStepsCol
        completedSteps={completedSteps}
        stepsCount={totalSteps}
      />
    ),
  },
];

type ToursSessionsBreakdownTableProps = {
  setCsvParams: (params: CsvParams) => void;
};

export const ToursSessionsBreakdownTable = ({
  setCsvParams,
}: ToursSessionsBreakdownTableProps) => {
  const { tourId, organizationId, workspaceId } = useTourPageProps();
  const [showAll, setShowAll] = useState(false);
  const [urlState, setUrlState] = useUrlState<UrlState>({});
  const { links } = urlState;

  const fetchArgs = useMemo<GetTourSessionsBreakdownArgs>(
    () => ({
      organizationId,
      shortSessions: true,
      internalSessions: true,
      workspaceIds: [workspaceId],
      timezone: getTimezoneName(),
      shareIds: links ? links?.split(',') : undefined,
      tourId: String(tourId),
    }),
    [links, organizationId, workspaceId, tourId]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScroll(useLazyGetTourSessionsBreakdownQuery);

  useEffect(() => {
    fetchFirstBatch(fetchArgs);
  }, [fetchArgs, fetchFirstBatch]);

  const clearFilters = () => {
    setUrlState({
      ...urlState,
      presenters: undefined,
      opportunities: undefined,
    });
  };

  useEffect(() => {
    setCsvParams({
      params: {
        ...fetchArgs,
        resourceTypes: ['tour'],
      },
      isDisabled: (items && !items.length) || !!error,
    });
  }, [fetchArgs, items, error, setCsvParams]);

  return (
    <>
      <div className="w-full flex items-center justify-between px-6 py-5">
        <div className="flex gap-3">
          <TourLinksFilter />
        </div>
        {!!items?.length && (
          <TextCaption
            color="text-ds-natural-500"
            label={`${
              totalSize > 7 && !showAll ? 7 : items.length
            } ${formatPlural('session', items.length)} (out of ${totalSize})`}
          />
        )}
      </div>
      <SessionsBreakdownTable
        showAll={showAll}
        setShowAll={setShowAll}
        totalSize={totalSize}
        onClear={clearFilters}
        error={error}
        handleFetchNextBatch={() => fetchNextBatch(fetchArgs)}
        cols={cols}
        data={items}
        isLoading={isLoading}
        isLoadingBatch={isLoadingBatch}
        noResults={links && !items?.length}
      />
    </>
  );
};
